import {
    Alert,
    Button,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalClose,
    ModalDialog,
    Option,
    Select,
    Stack,
    Typography,
} from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../../context";
import { APICreateAppointment, APIGetDoctors, APIGetPatients } from "../../api";

/**
 * Окно создания приёма пациента.
 *
 * Пропсы:
 *   open       — флаг открыто/закрыто
 *   onClose    — закрыть
 *   onCreated  — callback после создания (id новой записи)
 *   defaultPatientId / defaultDoctorId — опционально подставить заранее
 */
export default function ModalCreateAppointment({ open, onClose, onCreated, defaultPatientId, defaultDoctorId }) {
    const { token, currentDoctor } = useContext(LayoutContext);

    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const isAdmin =
        currentDoctor?.type_profile?.includes('admin') ||
        currentDoctor?.type_profile?.includes('supervisor');

    const [formData, setFormData] = useState({
        patient_id: '',
        doctor_id: '',
        date_appointment: '',
        response_deadline_hours: 24,
    });

    useEffect(() => {
        if (!open) return;
        setError(null);
        setFormData({
            patient_id: defaultPatientId || '',
            doctor_id: defaultDoctorId || (isAdmin ? '' : currentDoctor?.id) || '',
            date_appointment: new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16),
            response_deadline_hours: 24,
        });
        loadData();
        // eslint-disable-next-line
    }, [open]);

    const loadData = async () => {
        try {
            setLoading(true);
            const promises = [APIGetPatients(token, { page_size: 200 })];
            if (isAdmin) promises.push(APIGetDoctors(token, { page_size: 200 }));
            const [patRes, docRes] = await Promise.all(promises);
            setPatients(patRes?.profiles || []);
            if (docRes) setDoctors(docRes?.doctors || []);
        } catch (e) {
            setError('Не удалось загрузить списки');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.patient_id) { setError('Выберите пациента'); return; }
        if (isAdmin && !formData.doctor_id) { setError('Выберите врача'); return; }
        if (!formData.date_appointment) { setError('Укажите дату приёма'); return; }

        try {
            setSaving(true);
            setError(null);
            const payload = {
                patient_id: formData.patient_id,
                date_appointment: new Date(formData.date_appointment).toISOString(),
                response_deadline_hours: Number(formData.response_deadline_hours) || 24,
            };
            if (isAdmin) payload.doctor_id = formData.doctor_id;
            const response = await APICreateAppointment(token, payload);
            if (response?.id) {
                onCreated?.(response.id);
                onClose?.();
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Не удалось создать приём');
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 10011 }}>
            <ModalDialog size="md" sx={{ maxWidth: 500, width: '95%' }}>
                <ModalClose onClick={onClose} />
                <Typography level="h4" sx={{ mb: 2 }}>Создать приём</Typography>

                {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

                <Stack spacing={2}>
                    <FormControl required>
                        <FormLabel>Пациент</FormLabel>
                        <Select
                            slotProps={{ listbox: { sx: { zIndex: 10012 } } }}
                            placeholder={loading ? 'Загрузка…' : 'Выберите пациента'}
                            value={formData.patient_id}
                            onChange={(_, v) => setFormData(p => ({ ...p, patient_id: v }))}
                        >
                            {patients.map(p => (
                                <Option key={p.id} value={p.id}>
                                    {p.name} {p.phone ? `(${p.phone})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>

                    {isAdmin && (
                        <FormControl required>
                            <FormLabel>Врач</FormLabel>
                            <Select
                                slotProps={{ listbox: { sx: { zIndex: 10012 } } }}
                                placeholder={loading ? 'Загрузка…' : 'Выберите врача'}
                                value={formData.doctor_id}
                                onChange={(_, v) => setFormData(p => ({ ...p, doctor_id: v }))}
                            >
                                {doctors.map(d => (
                                    <Option key={d.id} value={d.id}>{d.name}</Option>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl required>
                        <FormLabel>Дата и время приёма</FormLabel>
                        <Input
                            type="datetime-local"
                            value={formData.date_appointment}
                            onChange={(e) => setFormData(p => ({ ...p, date_appointment: e.target.value }))}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Дедлайн ответа врача (часы)</FormLabel>
                        <Input
                            type="number"
                            slotProps={{ input: { min: 1, max: 720 } }}
                            value={formData.response_deadline_hours}
                            onChange={(e) => setFormData(p => ({ ...p, response_deadline_hours: e.target.value }))}
                        />
                    </FormControl>

                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Отмена</Button>
                        <Button color="primary" loading={saving} onClick={handleSubmit}>Создать</Button>
                    </Stack>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}
